import React, { useState, useEffect } from 'react';
import {
  Switch,
  TimePicker,
  InputNumber,
  Typography,
  Tooltip,
  Row,
  Col,
  Button,
  Space,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { StyleGuide } from 'styles/StyleGuide';
import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface AuditTemplateAlertRuleProps {
  orgId: string;
  auditTemplateId: string;
  alertRule?: {
    enabled: boolean;
    daysInterval: number;
    nextAlertTime: string;
  } | null;
  updateAuditTemplate: ({
    orgId,
    auditTemplateId,
    data,
  }: {
    orgId: string;
    auditTemplateId: string;
    data: {
      alertRule: {
        enabled: boolean;
        daysInterval: number;
        nextAlertTime: string;
      } | null;
    };
  }) => Promise<void>;
}

export default function AuditTemplateAlertRule({
  orgId,
  auditTemplateId,
  alertRule,
  updateAuditTemplate,
}: AuditTemplateAlertRuleProps) {
  const { t } = useTranslation();

  // State
  const [enabled, setEnabled] = useState(false);
  const [daysInterval, setDaysInterval] = useState<number>(7);
  const [time, setTime] = useState<Dayjs>(dayjs().hour(16).minute(0).second(0));
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize state from props
  useEffect(() => {
    if (alertRule) {
      setEnabled(alertRule.enabled);
      if (alertRule.daysInterval) {
        setDaysInterval(alertRule.daysInterval);
      }
      if (alertRule.nextAlertTime) {
        // Parse "HH:mm" string
        const [hours, minutes] = alertRule.nextAlertTime.split(':').map(Number);
        setTime(dayjs().hour(hours).minute(minutes).second(0));
      }
    } else {
      setEnabled(false);
      setDaysInterval(7);
      setTime(dayjs().hour(16).minute(0).second(0));
    }
    setIsDirty(false);
  }, [alertRule]);

  const onSave = async () => {
    setLoading(true);
    try {
      const timeString = time.format('HH:00'); // Store as HH:00 based on previous logic

      await updateAuditTemplate({
        orgId,
        auditTemplateId,
        data: {
          alertRule: {
            enabled: enabled,
            daysInterval: daysInterval,
            nextAlertTime: timeString,
          },
        },
      });
      message.success(t('Advarselsregel opdateret'));
      setIsDirty(false);
    } catch (error) {
      console.error('Error updating alert rule:', error);
      message.error(t('Kunne ikke opdatere advarselsregel'));
    } finally {
      setLoading(false);
    }
  };

  const onToggleEnable = (checked: boolean) => {
    setEnabled(checked);
    setIsDirty(true);
  };

  const onDaysIntervalChange = (val: number | null) => {
    if (val) {
      setDaysInterval(val);
      setIsDirty(true);
    }
  };

  const onTimeChange = (val: Dayjs | null) => {
    if (val) {
      const hourOnly = val.minute(0).second(0);
      setTime(hourOnly);
      setIsDirty(true);
    }
  };

  // Helper for human readable text
  const getRuleText = () => {
    if (!enabled) return t('Advarsel er deaktiveret');
    return `Hver ${daysInterval} dag(e) efter seneste udførelse kl. ${time.format('HH:00')}`;
  };

  return (
    <div>
      <div
        style={{
          marginTop: 16,
          borderBottom: `1px solid ${StyleGuide.palette.grey200}`,
          marginBottom: 16,
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tooltip title={t('Få en advarsel hvis opgaven ikke er udført inden for en given periode')}>
          <Text strong>
            {t('Advarselsregel')} <InfoCircleOutlined />
          </Text>
        </Tooltip>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={onSave}
          loading={loading}
          disabled={!isDirty}
          size="small"
        >
          {t('Gem')}
        </Button>
      </div>

      <div
        style={{
          marginTop: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text>{t('Aktiver advarsel')}</Text>
        <Switch checked={enabled} onChange={onToggleEnable} />
      </div>

      {enabled && (
        <div style={{ marginTop: 16 }}>
          <Row gutter={[0, 16]}>
            {/* Alert Interval Section */}
            <Col span={24}>
              <div
                style={{
                  padding: 8,
                  backgroundColor: StyleGuide.palette.grey50,
                  borderRadius: 4,
                  marginBottom: 8,
                }}
              >
                <Text strong style={{ fontSize: 13 }}>
                  Giv advarsel hvis ikke udført senest:
                </Text>
              </div>
            </Col>

            <Col span={24}>
              <Text style={{ fontSize: 12, color: StyleGuide.palette.grey500 }}>
                Antal dage siden sidste udførelse (f.eks. 7)
              </Text>
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                value={daysInterval}
                onChange={onDaysIntervalChange}
              />
            </Col>

            <Col span={24}>
              <Text style={{ fontSize: 12, color: StyleGuide.palette.grey500 }}>
                Tjek klokkeslæt
              </Text>
              <TimePicker
                style={{ width: '100%' }}
                value={time}
                format="HH:00"
                showMinute={false}
                onChange={onTimeChange}
                allowClear={false}
              />
            </Col>

            <Col span={24}>
              <div
                style={{
                  marginTop: 8,
                  padding: 10,
                  backgroundColor: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: 4,
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  📋 {getRuleText()}
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}
